import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import FilterSidebar from '@/pages/category/components/FilterSidebar';

const PAGE_SIZE = 8;

export default function Category() {
  const { type = '' } = useParams<{ type: string }>();
  const category = categories.find((c) => c.key === type);

  const [region, setRegion] = useState('不限');
  const [year, setYear] = useState('不限');
  const [sort, setSort] = useState('最新');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [panelOpen, setPanelOpen] = useState(false);

  const categoryName = category?.name ?? type ?? '影视';

  useDocumentTitle(`${categoryName} - 蜜桃网影视分类`, {
    description: `蜜桃网${categoryName}分类页，汇集最新${categoryName}资源，支持按地区、年份与热度筛选，快速找到你想看的${categoryName}。`,
    keywords: `蜜桃网,${categoryName},${categoryName}推荐,影视分类,影视资源`,
  });

  const filtered = useMemo(() => {
    let list = movies.filter((m) => m.type === categoryName);

    if (region !== '不限') {
      list = list.filter((m) => m.region === region);
    }

    if (year === '近一年') {
      list = list.filter((m) => m.year >= 2025);
    } else if (year === '近三年') {
      list = list.filter((m) => m.year >= 2023);
    } else if (year === '2020年前') {
      list = list.filter((m) => m.year < 2020);
    }

    if (sort === '最热') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => b.year - a.year);
    }

    return list;
  }, [categoryName, region, year, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setRegion('不限');
    setYear('不限');
    setSort('最新');
    setVisible(PAGE_SIZE);
  };

  const updateWith = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setVisible(PAGE_SIZE);
  };

  const filterProps = {
    region,
    year,
    sort,
    onRegion: updateWith(setRegion),
    onYear: updateWith(setYear),
    onSort: updateWith(setSort),
    onReset: resetFilters,
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-4 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{categoryName}</span>
      </nav>

      {category && (
        <div className="mb-8 rounded-lg border border-background-200/70 bg-background-100 p-6">
          <h1 className="flex items-center gap-2.5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${category.icon} text-xl`} />
            </span>
            {category.name}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr] lg:gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="mb-4 font-heading text-base font-semibold text-foreground-950">筛选条件</h2>
            <FilterSidebar {...filterProps} />
          </div>
        </aside>

        <div>
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-primary-700">{filtered.length}</span> 部作品
            </p>
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 lg:hidden"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 xl:grid-cols-4" data-product-shop>
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <div className="mt-8 flex justify-center">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="whitespace-nowrap rounded-full bg-primary-500 px-8 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    加载更多
                  </button>
                ) : (
                  <span className="text-sm text-foreground-400">— 已经到底啦 —</span>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              title="没有找到符合条件的影视资源"
              description="试试调整筛选条件，或重置筛选查看全部内容。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setPanelOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterSidebar {...filterProps} />
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}